<template>
  <div>
    <button @click="$emit('exit')" class="text-sm text-gray-500 hover:text-gray-700 mb-4">&larr; All games</button>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-16 text-gray-500">Loading words…</div>
    <div v-else-if="error" class="text-center py-16 text-red-500">{{ error }}</div>

    <!-- Playing -->
    <div v-else-if="!finished" class="bg-white rounded-3xl border border-gray-200 shadow-sm p-6 sm:p-8">
      <div class="flex items-center justify-between text-xs font-medium text-gray-400">
        <span>⚡ Speed Match</span>
        <span><span class="font-mono">{{ elapsed }}s</span> · {{ matchedCount }} / {{ pairs.length }}</span>
      </div>
      <div class="mt-2 mb-5 h-1.5 w-full rounded-full bg-gray-100">
        <div class="h-1.5 rounded-full bg-emerald-500 transition-all duration-300"
             :style="{ width: `${(matchedCount / pairs.length) * 100}%` }"></div>
      </div>
      <p class="text-sm text-gray-500 mb-4">Tap a Vietnamese word, then its English meaning.</p>

      <div class="grid grid-cols-2 gap-3">
        <div class="space-y-2.5">
          <button
            v-for="tile in leftColumn" :key="tile.key"
            @click="pick(tile)"
            :disabled="tile.matched"
            class="w-full px-3 py-3.5 rounded-xl border-2 text-center font-medium transition-all"
            :class="tileClass(tile)"
          >{{ tile.text }}</button>
        </div>
        <div class="space-y-2.5">
          <button
            v-for="tile in rightColumn" :key="tile.key"
            @click="pick(tile)"
            :disabled="tile.matched"
            class="w-full px-3 py-3.5 rounded-xl border-2 text-center font-medium transition-all"
            :class="tileClass(tile)"
          >{{ tile.text }}</button>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div v-else class="bg-white rounded-3xl border border-gray-200 shadow-sm p-8 text-center">
      <div class="text-4xl mb-2">⚡</div>
      <h2 class="text-2xl font-bold text-gray-900">Nice — {{ pairs.length }} pairs in {{ elapsed }}s!</h2>
      <p class="mt-2 text-gray-600">Score <span class="font-bold text-emerald-600">{{ score }}</span>
        <span v-if="bestScore"> · Best {{ bestScore }}</span></p>
      <p class="mt-1 text-emerald-600 font-medium">+{{ xpEarned }} XP<span v-if="xpEarned === 0" class="text-gray-400"> (daily cap reached)</span></p>
      <div class="mt-6 flex gap-3 justify-center">
        <button @click="restart" class="bg-emerald-600 text-white font-medium px-6 py-3 rounded-xl hover:bg-emerald-700">Play again</button>
        <button @click="$emit('exit')" class="bg-gray-100 text-gray-700 font-medium px-6 py-3 rounded-xl hover:bg-gray-200">Done</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { apiGet, apiPost } from '../../api/client'

defineEmits(['exit'])

const PAIR_COUNT = 6
const loading = ref(true)
const error = ref(null)
const pairs = ref([])
const tiles = ref([])
const firstPick = ref(null)
const matchedCount = ref(0)
const mistakes = ref(0)
const elapsed = ref(0)
const finished = ref(false)
const score = ref(0)
const xpEarned = ref(0)
const bestScore = ref(null)
let timer = null

const leftColumn = computed(() => tiles.value.filter(t => t.side === 'vn'))
const rightColumn = computed(() => tiles.value.filter(t => t.side === 'en'))

function tileClass(tile) {
  if (tile.matched) return 'border-emerald-500 bg-emerald-50 text-emerald-700 opacity-60'
  if (tile.wrong) return 'border-red-500 bg-red-50 text-red-700'
  if (firstPick.value && firstPick.value.key === tile.key) return 'border-emerald-500 bg-emerald-50 text-emerald-700'
  return 'border-gray-200 bg-white text-gray-800 hover:border-emerald-400'
}

function shuffle(arr) {
  return [...arr].sort(() => Math.random() - 0.5)
}

async function load() {
  loading.value = true
  error.value = null
  try {
    const data = await apiGet(`/api/v1/games/vocab_pool?count=${PAIR_COUNT * 3}`)
    const words = shuffle(data.words || []).slice(0, PAIR_COUNT)
    if (words.length < 4) { error.value = 'Practice a few lessons first to unlock games!'; return }
    pairs.value = words
    const vn = words.map(w => ({ key: `vn-${w.id}`, wordId: w.id, text: w.word_vietnamese, side: 'vn', matched: false, wrong: false }))
    const en = words.map(w => ({ key: `en-${w.id}`, wordId: w.id, text: w.word_english, side: 'en', matched: false, wrong: false }))
    tiles.value = [...shuffle(vn), ...shuffle(en)]
    startTimer()
  } catch (e) {
    error.value = 'Could not load the game.'
  } finally {
    loading.value = false
  }
}

function startTimer() {
  elapsed.value = 0
  timer = setInterval(() => { elapsed.value += 1 }, 1000)
}

function pick(tile) {
  if (tile.matched) return
  if (!firstPick.value) { firstPick.value = tile; return }
  if (firstPick.value.key === tile.key) { firstPick.value = null; return }
  if (firstPick.value.side === tile.side) { firstPick.value = tile; return }

  if (firstPick.value.wordId === tile.wordId) {
    markMatched(firstPick.value, tile)
    firstPick.value = null
    if (matchedCount.value === pairs.value.length) finish()
  } else {
    flashWrong(firstPick.value, tile)
    mistakes.value += 1
    firstPick.value = null
  }
}

function markMatched(a, b) {
  for (const t of tiles.value) if (t.key === a.key || t.key === b.key) t.matched = true
  matchedCount.value += 1
}

function flashWrong(a, b) {
  for (const t of tiles.value) if (t.key === a.key || t.key === b.key) t.wrong = true
  setTimeout(() => { for (const t of tiles.value) t.wrong = false }, 500)
}

async function finish() {
  clearInterval(timer)
  finished.value = true
  score.value = Math.max(50, 1000 - elapsed.value * 10 - mistakes.value * 25)
  try {
    const res = await apiPost('/api/v1/games/record', {
      game: 'speed_match', score: score.value, correct: pairs.value.length, total: pairs.value.length,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
    xpEarned.value = res?.xp_earned ?? 0
    bestScore.value = res?.best_score ?? null
  } catch (e) { /* score still shown locally */ }
}

function restart() {
  finished.value = false
  matchedCount.value = 0
  mistakes.value = 0
  firstPick.value = null
  load()
}

onMounted(load)
onUnmounted(() => clearInterval(timer))
</script>
